import React, { useEffect, useState } from "react";
import Icon from "./icons";
import ParamsTable from "./ParamsTable";
import AuthPanel from "./AuthPanel";
import BodyPanel from "./BodyPanel";
import { HTTP_METHODS, VISIBLE_TO_OPTIONS } from "./resourceTypes";
import Select from "../../../components/common/Select/Select";

/**
 * The endpoint builder — one endpoint of an HTTP resource (REST API, GraphQL).
 *
 * Everything here edits the selected endpoint only. The parent page owns the
 * module tree, the resource name and saving; this component is handed one
 * endpoint and a way to patch it.
 */

const TABS = [
  { key: "params", label: "Parameters" },
  { key: "auth", label: "Authentication" },
  { key: "headers", label: "Headers" },
  { key: "body", label: "Body" },
];

/* The shared Select takes { label, value } options, so the plain method list is
   shaped once here rather than mapped on every render. */
const METHOD_OPTIONS = HTTP_METHODS.map((method) => ({ label: method, value: method }));

export default function HttpResourceForm({ endpoint, onChange, onTest, test, errors = {} }) {
  const [tab, setTab] = useState("params");

  const set = (patch) => onChange(patch);

  /* Who may mark a resource as Witmeg's. The antd form rendered its "Witmeg API"
     switch for special users only and hid it from everyone else, so the radios
     are hidden the same way — the choice decides who can see the resource in the
     report editor (a global board lists only `isWitmegApi && visibleTo ===
     "global"`), which is not an ordinary user's to make.

     Auth Required stays visible to everyone, which is also how the antd form
     has had it since the field was added. */
  const canPickScope = localStorage.getItem("IsSpecialUser") === "true";
  const isWitmeg = endpoint.apiScope === "witmeg";

  /* A Witmeg API is always called with credentials, so the toggle is forced on
     and locked there — but only for the users who can see the radios that say
     so. A new resource starts on WITMEG for everyone (see emptyResourceItem), and a
     greyed toggle whose cause is hidden from you is just a broken control; an
     ordinary user therefore keeps the free toggle the antd form gives them. */
  const lockAuth = canPickScope && isWitmeg;

  /* Kept in step with the lock rather than only set when the radio is clicked,
     or an existing WITMEG resource saved with the flag off would show the toggle
     on and still save it off. */
  useEffect(() => {
    if (lockAuth && !endpoint.authRequired) onChange({ authRequired: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockAuth, endpoint.authRequired]);

  /* Switching back to public starts the toggle off again rather than leaving
     the forced value behind. */
  const setScope = (apiScope) =>
    set(apiScope === "witmeg" ? { apiScope, authRequired: true } : { apiScope, authRequired: false });

  return (
    <div className="rsce-http">
      {/* Nothing about the name here: it is edited in the sidebar, and that is
          where its spinner, tick and message belong — beside the field being
          typed into rather than as a banner across the form. */}
      {/* The row keeps its place when the radios are hidden, so Auth Required
          stays where it has always been rather than jumping to the left. */}
      <div className="rsce-scope-row">
        {canPickScope ? (
          <div className="rsce-scope-left">
            <div className="rsce-scope-radios">
              <label className="rsce-radio">
                <input type="radio" name="rsce-scope" checked={!isWitmeg} onChange={() => setScope("public")} />
                <span className="rsce-radio-label">Public API</span>
              </label>
              <label className="rsce-radio">
                <input type="radio" name="rsce-scope" checked={isWitmeg} onChange={() => setScope("witmeg")} />
                <span className="rsce-radio-label">WITMEG API</span>
              </label>
            </div>

            {/* Only a Witmeg API is offered to anyone, so the choice appears
                with it and disappears with it — the same nesting the antd form
                has, and the payload only carries visibleTo in that case. */}
            {isWitmeg && (
              <div className="rsce-visible-wrap">
                <span className="rsce-toggle-label">Visible To:</span>
                <Select
                  className="rsce-visible-pick"
                  options={VISIBLE_TO_OPTIONS}
                  value={{ value: endpoint.visibleTo || "global" }}
                  onChange={(option) => set({ visibleTo: option.value })}
                />
              </div>
            )}
          </div>
        ) : (
          <span />
        )}

        <div className="rsce-toggle-wrap">
          <span className="rsce-toggle-label">Auth Required:</span>
          <button
            type="button"
            role="switch"
            aria-checked={lockAuth ? true : Boolean(endpoint.authRequired)}
            disabled={lockAuth}
            title={lockAuth ? "A WITMEG API is always called with credentials" : undefined}
            className={`rsce-toggle ${lockAuth || endpoint.authRequired ? "is-on" : ""} ${
              lockAuth ? "is-locked" : ""
            }`}
            onClick={() => set({ authRequired: !endpoint.authRequired })}
          >
            <span className="rsce-toggle-knob" />
          </button>
        </div>
      </div>

      {/* Method, URL and the test control — one bar, as in the design.

          The pill takes the selected verb's colour, and the menu is tinted to
          match it — the verb class on the trigger themes both, so while POST is
          selected the whole menu reads blue rather than each row flashing a
          different hue as the cursor moves down it. */}
      <div className="rsce-url-bar">
        <Select
          className={`rsce-method-pick rsce-m-${endpoint.method.toLowerCase()}`}
          options={METHOD_OPTIONS}
          value={{ label: endpoint.method, value: endpoint.method }}
          onChange={(option) => set({ method: option.value })}
        />

        <input
          className={`rsce-url-input ${errors.url ? "has-error" : ""}`}
          type="text"
          placeholder="https://api.example.com/endpoint"
          value={endpoint.url}
          onChange={(e) => set({ url: e.target.value })}
        />

        <button type="button" className="rsce-test-btn" onClick={onTest} disabled={test.status === "pending"}>
          {test.status === "pending" ? "TESTING…" : "TEST API"}
        </button>
      </div>
      {errors.url && <div className="rsce-error-text">{errors.url}</div>}

      <div className="rsce-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`rsce-tab ${tab === t.key ? "is-active" : ""}`}
            key={t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rsce-tab-panel">
        {tab === "params" && (
          <ParamsTable rows={endpoint.params} errors={errors.params} onChange={(params) => set({ params })} />
        )}
        {tab === "auth" && <AuthPanel auth={endpoint.auth} onChange={(auth) => set({ auth })} />}
        {tab === "headers" && (
          <ParamsTable rows={endpoint.headers} errors={errors.headers} onChange={(headers) => set({ headers })} />
        )}
        {tab === "body" && <BodyPanel body={endpoint.body} errors={errors.body} onChange={(body) => set({ body })} />}
      </div>

      <TestResults test={test} />
    </div>
  );
}

/**
 * The Test Results panel. Shared shape with the DB form: a labelled box with a
 * status badge in its corner, empty until something has been run.
 */
export function TestResults({ test }) {
  return (
    <div className="rsce-results">
      <div className="rsce-results-label">Test Results</div>
      <div className={`rsce-results-box rsce-results-${test.status || "idle"}`}>
        {test.status === "success" && <span className="rsce-badge rsce-badge-ok">Successful</span>}
        {test.status === "error" && <span className="rsce-badge rsce-badge-fail">Testing Failed</span>}
        {test.status === "pending" && <span className="rsce-badge rsce-badge-pending">Testing…</span>}

        {test.message ? (
          <div className="rsce-results-body">
            <div className="rsce-results-message">{test.message}</div>
            {test.detail && <pre className="rsce-results-detail">{test.detail}</pre>}
          </div>
        ) : (
          <div className="rsce-results-placeholder">Nothing run yet.</div>
        )}
      </div>
    </div>
  );
}
